import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,Button,Container,FormControlLabel,IconButton,
  Paper,Skeleton,Stack,Switch,Typography,
} from '@mui/material';
import{Send} from "lucide-react";

import { memo, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aiService } from 'src/Services/ApiHandlers/aiService';
import classes from './AiChat.module.css';
import AiChatHistory from './AiChatHistory';

const AiChat = ({ initialData = undefined, showTooltip = false, onExpand }) => {
  const params = useParams();
  console.log({ params });
  const chatHistoryRef = useRef(null);
  const [message, setMessage] = useState('');
  const [mathFormat, setMathFormat] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
  }, [chatHistory]);

  const handleExpand = () => {
    const currentExpanded = isExpanded;
    setIsExpanded((state) => !state);
    onExpand?.(!currentExpanded);
  };

  // use this function when we don't have initial data
  const handleMessage = async (history) => {
    const content = {
      content: history,
      book_slug: params.id,
      chapter_slug: params.chapter,
      mathFormat,
      header_slug: params.header,
      isHeaderTwo: params.headertwo ? params.headertwo : null,
    };
    try {
      const response = await aiService.askQuestion(content);
      if (response.code === 200) {
        setChatHistory((state) => [
          ...state,
          { type: 'received', text: response.data.message.content, mathFormat: response.data.mathFormat },
        ]);
      } else {
        toast.error(response.message || 'Something went wrong');
      }
    } catch (error) {
      console.log(error);
    }
  };

  // use this function when we have initial data
  const handleMessageWithInitial = async (history) => {
    const content = { content: history, initialData, mathFormat };
    try {
      const response = await aiService.askQuestionWithData(content);
      if (response.code === 200) {
        setChatHistory((state) => [
          ...state,
          { type: 'received', text: response.data.message.content, mathFormat: response.data.mathFormat },
        ]);
      } else {
        toast.error(response.message || 'Something went wrong');
      }
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    }
  };

  const handleSendMessage = async () => {
    if (message.trim() !== '') {
      setIsLoading(true);
      const newHistory = [...chatHistory, { type: 'sent', text: message, mathFormat }];
      setChatHistory(newHistory);
      setMessage('');

      if (!initialData) {
        await handleMessage(newHistory);
      } else {
        await handleMessageWithInitial(newHistory);
      }

      setIsLoading(false);
    }
  };

  const formatChangeHandler = (event) => {
    setMathFormat(event.target.checked);
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        mb: '2rem',
        width: '100%',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          margin: '20px 0',
          border: '1px solid #ccc',
        }}
      >
        <Stack
          direction="row"
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            bgcolor: '#0066cc',
            color: 'white',
            padding: '10px 20px',
            borderTopRightRadius: '4px',
            borderTopLeftRadius: '4px',
          }}
        >
          <Typography variant="h5" gutterBottom>
            AI Tutor
          </Typography>
          {showTooltip && (
            <IconButton sx={{ color: 'white' }} onClick={handleExpand}>
              {!isExpanded ? (
                <OpenInNewIcon
                  sx={{
                    rotate: '-90deg',
                  }}
                />
              ) : (
                <OpenInNewIcon
                  sx={{
                    rotate: '90deg',
                  }}
                />
              )}
            </IconButton>
          )}
        </Stack>
        <Stack direction="row-reverse">
          <Box>
            <FormControlLabel
              control={<Switch checked={mathFormat} onChange={formatChangeHandler} />}
              label="Math Format"
            />
          </Box>
        </Stack>

        <Box
          ref={chatHistoryRef}
          sx={{
            height: '400px',
            overflowY: 'auto',
            my: '1rem',
            paddingLeft: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            pb: '1rem',
          }}
        >
          {chatHistory.map((chat, index) => (
            <AiChatHistory key={index} chat={chat} />
          ))}
          {isLoading && <AiChatSkeleton />}
        </Box>

        <Stack direction="row" sx={{ position: 'relative' }}>
          <textarea
            rows={2}
            placeholder="Ask me anything..."
            className={classes.input_area}
            disabled={isLoading}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyUp={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                handleSendMessage();
              }
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSendMessage}
            sx={{
              position: 'absolute',
              right: '5px',
              bottom: '10px',
              px: '5px',
            }}
            disabled={isLoading}
          >
            <Send />
          </Button>
        </Stack>
        <Typography variant="subtitle2" sx={{ textAlign: 'center', color: '#8d8d8d', fontSize: '0.75rem' }}>
          The AI Tutor can make mistakes. Please double-check responses.
        </Typography>
      </Paper>
    </Container>
  );
};

export default memo(AiChat);

const AiChatSkeleton = () => {
  return (
    <>
      <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
      <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
      <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
    </>
  );
};
